import { useCallback, useRef, useState } from "react";

const STORAGE_PREFIX = "column_order_";

/**
 * Generic hook to manage column ordering with localStorage persistence.
 *
 * Resolution order:
 *   1. User preference saved in localStorage (from drag-and-drop reordering)
 *   2. `defaultOrder` — hardcoded display order (e.g. from config/schemeOrder.ts)
 *   3. `serverOrder` — alphabetical order returned by the API
 *
 * Columns not present in the chosen order are appended at the end.
 * Saved columns that no longer exist in `serverOrder` are dropped.
 *
 * @param storageKey   Unique key for this view (appended to prefix).
 * @param serverOrder  The column identifiers as returned by the API.
 * @param defaultOrder Optional hardcoded default order (from global config).
 */
export function useColumnOrder(
  storageKey: string,
  serverOrder: string[],
  defaultOrder?: string[],
) {
  const fullKey = STORAGE_PREFIX + storageKey;
  const dragSourceIdx = useRef<number | null>(null);

  /** Keep only columns that exist in `current`, preserving `preferred` order. */
  const applyOrder = useCallback(
    (preferred: string[], current: string[]): string[] => {
      const currentSet = new Set(current);
      const ordered = preferred.filter((col) => currentSet.has(col));
      const orderedSet = new Set(ordered);
      const remaining = current.filter((col) => !orderedSet.has(col));
      return [...ordered, ...remaining];
    },
    [],
  );

  const loadOrder = useCallback((): string[] => {
    // 1. Try localStorage (user preference)
    try {
      const raw = localStorage.getItem(fullKey);
      if (raw) {
        const saved: string[] = JSON.parse(raw);
        if (Array.isArray(saved) && saved.length > 0) {
          return applyOrder(saved, serverOrder);
        }
      }
    } catch { /* corrupt data — fall through */ }

    // 2. Try hardcoded default order
    if (defaultOrder && defaultOrder.length > 0) {
      return applyOrder(defaultOrder, serverOrder);
    }

    // 3. Fall back to server order
    return serverOrder;
  }, [fullKey, serverOrder, defaultOrder, applyOrder]);

  const [columns, setColumns] = useState<string[]>(loadOrder);

  const persistAndSet = useCallback(
    (next: string[]) => {
      setColumns(next);
      try {
        localStorage.setItem(fullKey, JSON.stringify(next));
      } catch { /* storage full — ignore */ }
    },
    [fullKey],
  );

  /** Move a column from `fromIndex` to `toIndex`. */
  const reorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;
      const next = [...columns];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      persistAndSet(next);
    },
    [columns, persistAndSet],
  );

  /** Reset to default order and clear saved preference. */
  const resetOrder = useCallback(() => {
    const base = defaultOrder && defaultOrder.length > 0
      ? applyOrder(defaultOrder, serverOrder)
      : serverOrder;
    setColumns(base);
    try { localStorage.removeItem(fullKey); } catch { /* ignore */ }
  }, [serverOrder, defaultOrder, fullKey, applyOrder]);

  /** Sync with new server data (e.g. after refetch adds/removes schemes). */
  const sync = useCallback(
    (newServerOrder: string[]) => {
      // 1. Try localStorage
      try {
        const raw = localStorage.getItem(fullKey);
        if (raw) {
          const saved: string[] = JSON.parse(raw);
          if (Array.isArray(saved) && saved.length > 0) {
            setColumns(applyOrder(saved, newServerOrder));
            return;
          }
        }
      } catch { /* ignore */ }

      // 2. Try default order
      if (defaultOrder && defaultOrder.length > 0) {
        setColumns(applyOrder(defaultOrder, newServerOrder));
        return;
      }

      // 3. Server order
      setColumns(newServerOrder);
    },
    [fullKey, defaultOrder, applyOrder],
  );

  // --- Native drag-and-drop handlers for column headers ---
  const onDragStart = useCallback((index: number) => {
    dragSourceIdx.current = index;
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const onDrop = useCallback(
    (targetIndex: number) => {
      if (dragSourceIdx.current !== null && dragSourceIdx.current !== targetIndex) {
        reorder(dragSourceIdx.current, targetIndex);
      }
      dragSourceIdx.current = null;
    },
    [reorder],
  );

  return { columns, reorder, resetOrder, sync, onDragStart, onDragOver, onDrop } as const;
}